import {
	Box,
	Divider,
	Group,
	ListViewGroup,
	ListViewRow,
	Stack,
	Text,
} from '@ekhodealer/ekho-common/rev';

import { DetailedInfoLineItem } from '@common/components/DetailedInfoLineItem';
import { EmptySection } from '@common/components/EmptySection';
import { formatCurrency } from '@common/lib/currency';

type BaseTransaction = {
	label: string;
	amount: number;
	date: string;
	status: string;
	source: string;
};

type TransactionWithRefunds = BaseTransaction & {
	refunds?: Array<{
		label: string;
		amount: number;
		date: string;
	}>;
};

type TransactionItem = BaseTransaction | TransactionWithRefunds;

interface TransactionListProps {
	/** Array of transactions to display */
	transactions: TransactionItem[] | undefined;
	/** Label for the total (e.g., "Total bank deposits") */
	totalLabel: string;
	/** Empty message to show when there are no transactions */
	emptyMessage: string;
	/** Whether to show the total section at the bottom. Defaults to true */
	includeTotal?: boolean;
}

/**
 * TransactionList component that displays transactions in two sections:
 * - Processing: transactions with PROCESSING or PENDING status
 * - Transaction history: all other transactions
 */
export const TransactionList = ({
	transactions,
	totalLabel,
	emptyMessage,
	includeTotal = true,
}: TransactionListProps) => {
	const displayTransactions = transactions || [];

	if (!displayTransactions || displayTransactions.length === 0) {
		return <EmptySection message={emptyMessage} />;
	}

	// Calculate total: sum of all transaction amounts (excluding failed) minus all refunds
	const total = displayTransactions.reduce((sum, transaction) => {
		// Skip failed transactions
		if (transaction.status.toUpperCase() === 'FAILED') {
			return sum;
		}

		// Add transaction amount
		let transactionTotal = transaction.amount;

		// Subtract refunds if they exist
		if ('refunds' in transaction && transaction.refunds && transaction.refunds.length > 0) {
			const refundsTotal = transaction.refunds.reduce(
				(refundSum, refund) => refundSum + refund.amount,
				0,
			);
			transactionTotal -= refundsTotal;
		}

		return sum + transactionTotal;
	}, 0);

	const formatSecondaryLabel = (transaction: TransactionItem) => {
		const parts = [transaction.date, transaction.source].filter(Boolean);
		return parts.join(' • ');
	};

	// Split transactions into processing and transaction history
	const processingTransactions = displayTransactions.filter(
		(transaction) =>
			transaction.status.toUpperCase() === 'PROCESSING' ||
			transaction.status.toUpperCase() === 'PENDING',
	);
	const transactionHistory = displayTransactions.filter(
		(transaction) =>
			transaction.status.toUpperCase() !== 'PROCESSING' &&
			transaction.status.toUpperCase() !== 'PENDING',
	);

	const TransactionDetailInfoItem = ({ transaction }: { transaction: TransactionItem }) => {
		const hasRefunds =
			'refunds' in transaction && transaction.refunds && transaction.refunds.length > 0;
		const refunds =
			hasRefunds && 'refunds' in transaction && transaction.refunds
				? transaction.refunds
				: [];

		return (
			<>
				<DetailedInfoLineItem
					primaryLabel={transaction.label}
					secondaryLabel={formatSecondaryLabel(transaction)}
					tertiaryLabel={transaction.status}
					rightAccessory={
						<Text size='bodySmall' align='right' weight='semibold'>
							{formatCurrency(transaction.amount)}
						</Text>
					}
				/>
				{hasRefunds && refunds.length > 0 && (
					<Stack itemsSpacing='8' padding={{ x: '80', top: '8' }}>
						{refunds.map((refund, refundIndex) => (
							<DetailedInfoLineItem
								key={refundIndex}
								primaryLabel={refund.label}
								secondaryLabel={refund.date}
								rightAccessory={
									<Text size='bodySmall' align='right'>
										({formatCurrency(refund.amount)})
									</Text>
								}
							/>
						))}
					</Stack>
				)}
			</>
		);
	};

	return (
		<Stack itemsSpacing='16'>
			{processingTransactions.length > 0 && (
				<>
					<Text size='footnote' weight='semibold'>
						Processing
					</Text>
					<ListViewGroup aria-label='Processing transactions'>
						{processingTransactions.map((transaction, index) => (
							<ListViewRow
								key={index}
								bottomContent={
									<TransactionDetailInfoItem transaction={transaction} />
								}
							/>
						))}
					</ListViewGroup>
				</>
			)}

			{transactionHistory.length > 0 && (
				<>
					<Text size='footnote' weight='semibold'>
						Transaction history
					</Text>
					<ListViewGroup aria-label='Transaction history'>
						{transactionHistory.map((transaction, index) => (
							<ListViewRow
								key={index}
								bottomContent={
									<TransactionDetailInfoItem transaction={transaction} />
								}
							/>
						))}
					</ListViewGroup>
				</>
			)}

			{includeTotal && (
				<>
					<Divider />
					<Box>
						<Group itemsAlignX='space-between' itemsAlignY='center' noWrap>
							<Group.Item>
								<Text size='bodySmall' weight='semibold' align='left'>
									{totalLabel}
								</Text>
							</Group.Item>
							<Group.Item>
								<Text size='bodySmall' weight='semibold' align='right'>
									{formatCurrency(total)}
								</Text>
							</Group.Item>
						</Group>
					</Box>
				</>
			)}
		</Stack>
	);
};
